// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for header actions area
// - Inputs: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange on hover
// - Checkbox: blue default, orange when checked
// - No business/API logic changed

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Checkbox,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Sections = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    CampusCode: "",
    Section: "",
    SectionOrder: "",
    IsActive: 1,
  });
  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [campusCode, setCampusCode] = useState("");
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");

  const downloadSampleExcel = () => {
    const cc = campusCode || newRow.CampusCode || "CAMPUS01";
    const headers = ["CampusCode", "Section", "SectionOrder", "IsActive"];

    const sampleRows = [
      { CampusCode: cc, Section: "A", SectionOrder: 1, IsActive: 1 },
      { CampusCode: cc, Section: "B", SectionOrder: 2, IsActive: 1 },
      { CampusCode: cc, Section: "C", SectionOrder: 3, IsActive: 0 },
    ];

    const normalized = sampleRows.map((r) => {
      const obj = {};
      headers.forEach((h) => (obj[h] = r[h] ?? ""));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(normalized, { header: headers });
    ws["!cols"] = [
      { wch: 14 }, // CampusCode
      { wch: 12 }, // Section
      { wch: 14 }, // SectionOrder
      { wch: 10 }, // IsActive
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sections");

    const instructions = [
      ["Field", "Notes / Example"],
      ["CampusCode", "Auto-filled from login (don’t change)"],
      ["Section", "Section name (e.g., A / B / C / A1)"],
      ["SectionOrder", "Numeric order (e.g., 1, 2, 3)"],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 18 }, { wch: 55 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Sections_Sample_${cc}.xlsx`);
  };

  // 1) Decode campus code from JWT once
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
      setNewRow((r) => ({ ...r, CampusCode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, []);

  // 2) Central fetch function
  const fetchSections = async () => {
    if (!campusCode) return;
    try {
      const res = await axios.get(`${API_URL}/api/Sections`, {
        //params: { CampusCode: campusCode },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      setError("Failed to fetch data");
    }
  };

  // 3) Fetch on mount & when campusCode changes
  useEffect(() => {
    fetchSections();
  }, [campusCode]);

  // Unified handler for TextField & Checkbox
  const handleInputChange = (e, id = null) => {
    const { name, type, value, checked } = e.target;
    const val = type === "checkbox" ? (checked ? 1 : 0) : value;

    if (id == null) {
      setNewRow((r) => ({ ...r, [name]: val }));
    } else {
      setData((rows) => rows.map((r) => (r.id === id ? { ...r, [name]: val } : r)));
    }
  };

  // Toggle active for new row
  const handleNewRowActiveToggle = (e) => {
    setNewRow((r) => ({ ...r, IsActive: e.target.checked ? 1 : 0 }));
  };

  // Add
  const handleAddRow = async () => {
    try {
      await axios.post(`${API_URL}/api/Sections`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setNewRow({
        CampusCode: campusCode,
        Section: "",
        SectionOrder: "",
        IsActive: 1,
      });
      await fetchSections();
    } catch (err) {
      if (err.response?.status === 409) setError(err.response.data.error);
      else setError("Failed to add row");
    }
  };

  // Update
  const handleUpdateRow = async (id) => {
    const updated = data.find((r) => r.id === id);
    try {
      await axios.put(`${API_URL}/api/Sections/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setEditRowId(null);
      await fetchSections();
    } catch {
      setError("Failed to update row");
    }
  };

  // Delete
  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/Sections/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      await fetchSections();
    } catch (err) {
      if (err.response?.status === 409) setError(err.response.data.error);
      else setError("Failed to delete row");
    }
  };

  // Bulk upload
  const handleBulkClick = () => fileInputRef.current.click();
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    try {
      await axios.post(`${API_URL}/api/UploadSections`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      await fetchSections();
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Sections
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Add, edit, activate/deactivate, or bulk upload sections
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mb: 1,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Table */}
      {/* OUTER – keeps rounded corners & spacing */}
      <Paper elevation={0} sx={tableWrapSx}>

        {/* INNER – ONLY for horizontal scroll */}
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: "3%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "35%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  {["#", "Section", "Order", "Active", "Actions"].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, idx) =>
                  editRowId === row.id ? (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>

                      <TableCell>
                        <TextField
                          name="Section"
                          value={row.Section}
                          onChange={(e) => handleInputChange(e, row.id)}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          name="SectionOrder"
                          value={row.SectionOrder}
                          onChange={(e) => handleInputChange(e, row.id)}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </TableCell>

                      <TableCell>
                        <Checkbox
                          name="IsActive"
                          checked={!!row.IsActive}
                          onChange={(e) => handleInputChange(e, row.id)}
                          sx={checkboxSx}
                        />
                      </TableCell>

                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                          <SaveIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.Section}</TableCell>
                      <TableCell>{row.SectionOrder}</TableCell>
                      <TableCell>
                        <Checkbox checked={!!row.IsActive} readOnly sx={checkboxSx} />
                      </TableCell>
                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => setEditRowId(row.id)} title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {/* New Row */}
                <TableRow sx={zebraRowSx(data.length)}>
                  <TableCell>{data.length + 1}</TableCell>

                  <TableCell>
                    <TextField
                      name="Section"
                      value={newRow.Section}
                      onChange={handleInputChange}
                      placeholder="Section"
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      name="SectionOrder"
                      value={newRow.SectionOrder}
                      onChange={handleInputChange}
                      placeholder="Order"
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell>
                    <Checkbox
                      name="IsActive"
                      checked={!!newRow.IsActive}
                      onChange={handleNewRowActiveToggle}
                      sx={checkboxSx}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default Sections;
